/**
 * Checkout market presets shared by QA Generate Users and Braze attribute guidance.
 * Brooklyn `POST /v2/billing/checkout` uses `billing.country`, `billing.currency`, and `billing.postalCode`.
 */

import { USER_LANGUAGE_DE, USER_LANGUAGE_EN, USER_LANGUAGE_ES, USER_LANGUAGE_FR } from '../language'

export type BillingMarketPreset = {
  /** Stable key for Select `value` */
  id: string
  /** User-facing label */
  label: string
  /** ISO 3166-1 alpha-2 */
  country: string
  /** ISO 4217 */
  currency: string
  /** Example postal / ZIP valid for that market (operators can edit). */
  suggestedPostalCode: string
  /**
   * Default app language on the user record (`en` | `es` | `de` | `fr`).
   */
  defaultAppLanguage: string
}

export const BILLING_MARKET_PRESETS: readonly BillingMarketPreset[] = [
  {
    id: 'US_USD',
    label: 'United States — USD · English',
    country: 'US',
    currency: 'USD',
    suggestedPostalCode: '80503',
    defaultAppLanguage: USER_LANGUAGE_EN,
  },
  {
    id: 'GB_GBP',
    label: 'United Kingdom — GBP · English',
    country: 'GB',
    currency: 'GBP',
    suggestedPostalCode: 'SW1A 1AA',
    defaultAppLanguage: USER_LANGUAGE_EN,
  },
  {
    id: 'AU_AUD',
    label: 'Australia — AUD · English',
    country: 'AU',
    currency: 'AUD',
    suggestedPostalCode: '2000',
    defaultAppLanguage: USER_LANGUAGE_EN,
  },
  {
    id: 'NZ_NZD',
    label: 'New Zealand — NZD · English',
    country: 'NZ',
    currency: 'NZD',
    suggestedPostalCode: '1010',
    defaultAppLanguage: USER_LANGUAGE_EN,
  },
  {
    id: 'CA_CAD',
    label: 'Canada — CAD · English',
    country: 'CA',
    currency: 'CAD',
    suggestedPostalCode: 'M5V 3A8',
    defaultAppLanguage: USER_LANGUAGE_EN,
  },
  {
    id: 'MX_MXN',
    label: 'Mexico — MXN · Español',
    country: 'MX',
    currency: 'MXN',
    suggestedPostalCode: '06600',
    defaultAppLanguage: USER_LANGUAGE_ES,
  },
  {
    id: 'BR_USD',
    label: 'Brazil — USD · Español',
    country: 'BR',
    currency: 'USD',
    suggestedPostalCode: '01310-100',
    defaultAppLanguage: USER_LANGUAGE_ES,
  },
  {
    id: 'DE_EUR',
    label: 'Germany — EUR · Deutsch',
    country: 'DE',
    currency: 'EUR',
    suggestedPostalCode: '10115',
    defaultAppLanguage: USER_LANGUAGE_DE,
  },
  {
    id: 'FR_EUR',
    label: 'France — EUR · Français',
    country: 'FR',
    currency: 'EUR',
    suggestedPostalCode: '75001',
    defaultAppLanguage: USER_LANGUAGE_FR,
  },
] as const

export function findBillingMarketPreset (id: string): BillingMarketPreset | undefined {
  return BILLING_MARKET_PRESETS.find((p) => p.id === id)
}

function formatBillingMarketCountryLabel (preset: BillingMarketPreset): string {
  const regionName = preset.label.split(' — ')[0]
  const currencyNote =
    preset.country === 'BR' && preset.currency === 'USD' ? 'USD via LTM' : preset.currency
  return `${preset.country} — ${regionName} (${currencyNote})`
}

/** Unique ISO-2 country codes from checkout market presets (for Braze country combobox). */
export function getBillingMarketCountryOptions (): Array<{ value: string; label: string }> {
  const seen = new Set<string>()
  const options: Array<{ value: string; label: string }> = []
  BILLING_MARKET_PRESETS.forEach((preset) => {
    if (seen.has(preset.country)) {
      return
    }
    seen.add(preset.country)
    options.push({
      value: preset.country,
      label: formatBillingMarketCountryLabel(preset),
    })
  })
  return options
}
